import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { useEffect, useState, useRef, useContext } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { Breadcrumbs, IconButton, InputAdornment, ListItemButton, TextField, Typography, LinearProgress, Button } from '@mui/material'

import { requestAccessToken } from '../api/auth.api'
import NextIcon from '../assets/icons/next-icon.svg'
import HelpIcon from '../assets/icons/help-icon.svg'
// import { getPaymentTickets } from '../api/payments.api'
import Pagination from '../components/common/pagination'
import SearchIcon from '../assets/icons/search-icon.svg'
import { handleIsValidMatch } from '../utils/common.utils'
import { CustomerCompany, FormError, Payment } from '../types'
import { PaymentTagType, PaymentWithNoveltiesType } from '../lib'
import { AppStateContext, AuthContext, UserContext } from '../contexts'
import { baseButtonBlueBg, textFieldWhiteBg } from '../theme/custom-styles'
import { getPaymentsWithAlertsBySearch, getPaymentsWithAlertsData } from '../api/app-data.api'
import DocumentPaymentListItem from '../components/document-payments-details-management/document-payment-list-item'

const PaymentsWithAlertsManagement = () => {
  const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'

  const navigate = useNavigate()
  const location = useLocation()
  const { customerCompanyId } = useParams()
  const currentListPageRef = useRef<number>(1)
  const paymentToSearchRef = useRef<string>('')
  const { userSession } = useContext(UserContext)
  const topEntranceDistanceRef = useRef<number>(0)
  const dataFullyLoadedRef = useRef<boolean>(false)
  const currentLoadingDataRef = useRef<boolean>(true)
  const { setScrollToTop } = useContext(AppStateContext)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const paymentsDisplayedDataRef = useRef<Array<Payment>>([])
  const { isAuth, removeAuth, activateAuth } = useContext(AuthContext)
  const { t } = useTranslation(['customer-company-details-management'])
  const selectedMainFilterRef = useRef<PaymentWithNoveltiesType>(PaymentWithNoveltiesType.WITH_FINDINGS)

  const [errors, setErrors] = useState<FormError>({})
  const [pageSize, setPageSize] = useState<number>(50)
  const [topDistance, setTopDistance] = useState<number>(0)
  const [hasMultipleRows, setHasMultipleRows] = useState(false)
  const [currentListPage, setCurrentListPage] = useState<number>(1)
  const [paymentToSearch, setPaymentToSearch] = useState<string>('')
  // const [paymentsData, setPaymentsData] = useState<Array<Payment>>([])
  const [searchingInDatabase, setSearchingInDatabase] = useState<boolean>(false)
  const [paymentsWithFindingsData, setPaymentsWithFindingsData] = useState<Array<Payment>>([])
  const [noRegistriesFoundAtDatabase, setNoRegistriesFoundAtDatabase] = useState<boolean>(false)
  const [paymentsWithGeneralAlertsData, setPaymentsWithGeneralAlertsData] = useState<Array<Payment>>([])
  const [paymentsWithHardStopData, setPaymentsWithHardStopData] = useState<Array<Payment>>([])
  const [loadingLocalData, setLoadingLocalData] = useState<boolean>(true)
  // const [topEntranceDistance, setTopEntranceDistance] = useState<number>(0)
  const [paymentsListData, setPaymentsListData] = useState<Array<Payment>>([])
  const [searchedPaymentsData, setSearchedPaymentsData] = useState<Array<Payment>>([])
  const [paymentsDisplayedData, setPaymentsDisplayedData] = useState<Array<Payment>>([])
  const [customerCompanyData, setCustomerCompanyData] = useState<CustomerCompany | null>(null)
  const [paymentsResponseStatus, setPaymentsResponseStatus] = useState<Array<{ paymentId: string; status: string }>>([])
  const [selectedMainFilter, setSelectedMainFilter] = useState<PaymentWithNoveltiesType>(PaymentWithNoveltiesType.WITH_FINDINGS)

  useEffect(() => {
    if (!userSession) {
      return
    }

    if (userSession && userSession?.role !== 'super-admin') {
      navigate('/404')
      return
    }

    handleGetPaymentsWithAlerts()

    const checkRows = () => {
      if (!containerRef.current) return

      const container = containerRef.current
      const firstItem = container.firstElementChild
      if (!firstItem) return

      const firstItemTop = firstItem.getBoundingClientRect().top
      const multipleRows = Array.from(container.children).some((item) => item.getBoundingClientRect().top > firstItemTop)

      setHasMultipleRows(multipleRows)
    }

    const searchParams = new URLSearchParams(location.search)

    const n = searchParams.get('n')
    const filter = searchParams.get('filter')

    if (filter) {
      setSelectedMainFilter(Number(filter))
      selectedMainFilterRef.current = Number(filter)
    }

    if (n) topEntranceDistanceRef.current = Number(n)

    const scrollContainer = document.querySelector('.section-content-container')
    const targetElement = document.querySelector('.section-container')

    if (scrollContainer instanceof HTMLElement && targetElement instanceof HTMLElement) {
      const handleScroll = () => {
        const rect = targetElement.getBoundingClientRect()
        const containerRect = scrollContainer.getBoundingClientRect()
        setTopDistance(rect.top - containerRect.top)
      }

      scrollContainer.addEventListener('scroll', handleScroll, { passive: true })
      handleScroll()

      return () => scrollContainer.removeEventListener('scroll', handleScroll)
    }

    const resizeObserver = new ResizeObserver(checkRows)

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current)
    }

    return () => resizeObserver.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSession])

  useEffect(() => {
    const firstPageIndex = (currentListPage - 1) * pageSize
    const lastPageIndex = firstPageIndex + pageSize
    const paymentsPerPage = paymentsDisplayedData.slice(firstPageIndex, lastPageIndex)

    setPaymentsListData(paymentsPerPage)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentListPage, paymentsDisplayedData])

  useEffect(() => {
    console.log('entro', 'selectedMainFilterRef.current', selectedMainFilterRef.current)
    handleSetPaymentToSearch('', false)

    // const paymentsToDisplay =
    //   selectedMainFilterRef.current === PaymentWithNoveltiesType.WITH_FINDINGS
    //     ? paymentsData.filter((paymentData: Payment) => paymentData.status !== 'waiting-for-resolution' && !('complianceResolutionData' in paymentData))
    //     : selectedMainFilterRef.current === PaymentWithNoveltiesType.GENERAL_ALERT
    //     ? paymentsData.filter((paymentData: Payment) => {
    //         if (paymentData.status === 'waiting-for-resolution' /* || (paymentData.status !== 'waiting-for-resolution' && 'complianceResolutionData' in paymentData) */) {
    //           const dailyPaymentTags = paymentData.tags ? paymentData.tags.find((tagData) => tagData.type === PaymentTagType.TO_CONFIRM_ORIGIN_OF_FUNDS_DAILY) : null
    //           const blackListedPaymentTags = paymentData.tags ? paymentData.tags.find((tagData) => tagData.type === PaymentTagType.BLACK_LISTED) : null
    //           const waitingForResolutionOfHardStopPaymentTags = paymentData.tags ? paymentData.tags.find((tagData) => tagData.type === PaymentTagType.TO_CONFIRM_PREVIOUS_PAYMENT_WAITING_FOR_RESOLUTION) : null

    //           if (!dailyPaymentTags && !blackListedPaymentTags && !waitingForResolutionOfHardStopPaymentTags) {
    //             return paymentData
    //           }
    //         }

    //         return false
    //       })
    //     : paymentsData.filter((paymentData: Payment) => {
    //         if (
    //           paymentData.status === 'waiting-for-resolution' /*  ||
    //           (paymentData.status !== 'waiting-for-resolution' && 'complianceResolutionData' in paymentData) ||
    //           (paymentData.status !== 'deleted' && 'complianceResolutionData' in paymentData) */
    //         ) {
    //           const dailyPaymentTags = paymentData.tags ? paymentData.tags.find((tagData) => tagData.type === PaymentTagType.TO_CONFIRM_ORIGIN_OF_FUNDS_DAILY) : null
    //           const blackListedPaymentTags = paymentData.tags ? paymentData.tags.find((tagData) => tagData.type === PaymentTagType.BLACK_LISTED) : null
    //           const waitingForResolutionOfHardStopPaymentTags = paymentData.tags ? paymentData.tags.find((tagData) => tagData.type === PaymentTagType.TO_CONFIRM_PREVIOUS_PAYMENT_WAITING_FOR_RESOLUTION) : null

    //           if (dailyPaymentTags || blackListedPaymentTags || waitingForResolutionOfHardStopPaymentTags) {
    //             return paymentData
    //           }
    //         }

    //         return false
    //       })

    const paymentsToDisplay =
      selectedMainFilterRef.current === PaymentWithNoveltiesType.WITH_FINDINGS
        ? [...paymentsWithFindingsData]
        : selectedMainFilterRef.current === PaymentWithNoveltiesType.GENERAL_ALERT
          ? [...paymentsWithGeneralAlertsData]
          : [...paymentsWithHardStopData]
    console.log('paymentsToDisplay', paymentsToDisplay, 'paymentsWithHardStopData')

    paymentsToDisplay.sort((thisPayment: Partial<Payment>, nextPayment: Partial<Payment>) => {
      const thisCreatedAt = thisPayment.createdAt instanceof Date ? thisPayment.createdAt.toISOString() : thisPayment.createdAt
      const nextCreatedAt = nextPayment.createdAt instanceof Date ? nextPayment.createdAt.toISOString() : nextPayment.createdAt

      if (thisCreatedAt && nextCreatedAt) {
        return Date.parse(nextCreatedAt) - Date.parse(thisCreatedAt)
      }

      return 0
    })

    if (selectedMainFilter === PaymentWithNoveltiesType.HARD_STOP) {
      handleGetTicketResponseStatus(paymentsToDisplay)
    }

    setPaymentsDisplayedData(paymentsToDisplay)
    paymentsDisplayedDataRef.current = paymentsToDisplay
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMainFilter])

  const handleGetPaymentsWithAlerts = async () => {
    let localPaymentsWithAlertsToSet: any = {}
    const localPaymentsWithAlerts = globalThis.sessionStorage.getItem(`paymentsWithAlerts:${customerCompanyId}:${selectedCountry}`)

    if (localPaymentsWithAlerts) {
      localPaymentsWithAlertsToSet = JSON.parse(localPaymentsWithAlerts)
      handleSetInitialData(localPaymentsWithAlertsToSet)
    }

    try {
      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      const { data } = await getPaymentsWithAlertsData(String(customerCompanyId), selectedCountry, accessToken)

      handleSetInitialData(data)
      globalThis.sessionStorage.setItem(`paymentsWithAlerts:${customerCompanyId}:${selectedCountry}`, JSON.stringify(data))
    } catch (error) {
      console.log('Error handleGetPaymentsWithAlerts > ', error)
    }
  }

  const handleSetInitialData = async (dataToSet: any) => {
    setCustomerCompanyData(dataToSet.customerCompanyData)
    handleHowToDisplayComponentData(dataToSet.paymentsWithAlertsData, dataToSet.paymentsWithHardStopData)
    dataFullyLoadedRef.current = true
  }

  const handleHowToDisplayComponentData = (paymentsWithAlerts: Array<any>, paymentsWithHardStop: Array<any>) => {
    const paymentsWithFindings = paymentsWithAlerts.filter(
      (paymentData: Payment) =>
        paymentData.status !== 'waiting-for-resolution' && !('complianceResolutionData' in paymentData) && (!('tags' in paymentData) || (Array.isArray(paymentData.tags) && paymentData.tags.length === 0))
    )

    const paymentsWithGeneralAlert = paymentsWithAlerts.filter(
      (paymentData: Payment) =>
        Array.isArray(paymentData.tags) &&
        paymentData.tags.length > 0 &&
        !!paymentData.tags?.some((tag) => tag.type === PaymentTagType.TO_CONFIRM_ORIGIN_OF_FUNDS_MONTHLY || tag.type === PaymentTagType.TO_CONFIRM_ORIGIN_OF_FUNDS_WEEKLY)
    )

    const paymentsToDisplay =
      selectedMainFilterRef.current === PaymentWithNoveltiesType.WITH_FINDINGS
        ? [...paymentsWithFindings]
        : selectedMainFilterRef.current === PaymentWithNoveltiesType.GENERAL_ALERT
          ? [...paymentsWithGeneralAlert]
          : Array.isArray(paymentsWithHardStopData) && PaymentWithNoveltiesType.HARD_STOP
            ? [...paymentsWithHardStop]
            : []

    paymentsToDisplay.sort((thisPayment: Partial<Payment>, nextPayment: Partial<Payment>) => {
      const thisCreatedAt = thisPayment.createdAt instanceof Date ? thisPayment.createdAt.toISOString() : thisPayment.createdAt
      const nextCreatedAt = nextPayment.createdAt instanceof Date ? nextPayment.createdAt.toISOString() : nextPayment.createdAt

      if (thisCreatedAt && nextCreatedAt) {
        return Date.parse(nextCreatedAt) - Date.parse(thisCreatedAt)
      }

      return 0
    })

    if (selectedMainFilterRef.current === PaymentWithNoveltiesType.HARD_STOP) {
      handleGetTicketResponseStatus(paymentsToDisplay)
    }

    if (paymentToSearchRef.current === '' || paymentToSearchRef.current === null) {
      const currentListPageDataState = currentListPageRef.current
      const firstPageIndex = currentListPageDataState !== null ? (currentListPageDataState - 1) * pageSize : 0
      const lastPageIndex = firstPageIndex + pageSize
      const paymentsPerPage = paymentsToDisplay.slice(firstPageIndex, lastPageIndex)

      setPaymentsListData(paymentsPerPage)
    }

    setPaymentsDisplayedData(paymentsToDisplay)
    setPaymentsWithFindingsData(paymentsWithFindings)
    setPaymentsWithGeneralAlertsData(paymentsWithGeneralAlert)
    setPaymentsWithHardStopData(paymentsWithHardStop)
    paymentsDisplayedDataRef.current = paymentsToDisplay

    if (currentLoadingDataRef.current === true) {
      currentLoadingDataRef.current = false

      setTimeout(() => {
        setLoadingLocalData(false)
      }, 3000)

      const targetElement = document.querySelector('.section-content-container') as HTMLElement

      if (targetElement) {
        setTimeout(() => {
          targetElement.scrollTo({
            behavior: 'smooth',
            top: Math.abs(topEntranceDistanceRef.current)
          })
        }, 200)
      }
    }
  }

  const handleGetTicketResponseStatus = async (paymentsToSearch: Array<Payment>) => {
    if (paymentsToSearch.length === 0) return

    // const {
    //   data: { accessToken }
    // } = await requestAccessToken(String(isAuth).replaceAll('"', ''))
    const paymentResponseStatusUpdated = [] as Array<{ paymentId: string; status: string }>

    for (let i = 0; i < paymentsToSearch.length; i++) {
      // const {
      //   data: { paymentTickets }
      // } = await getPaymentTickets(String(paymentsToSearch[i]?._id), accessToken)
      const paymentAlreadyAddedIndex = paymentResponseStatusUpdated.findIndex((paymentData) => String(paymentData.paymentId) === String(paymentsToSearch[i]._id))

      if (paymentsToSearch[i].complianceResolutionData && paymentsToSearch[i].complianceResolutionData?.rejected) {
        if (paymentAlreadyAddedIndex > -1) {
          paymentResponseStatusUpdated[paymentAlreadyAddedIndex].status = 'rejected'
        } else {
          paymentResponseStatusUpdated.push({
            status: 'rejected',
            paymentId: String(paymentsToSearch[i]?._id)
          })
        }
      }

      if (paymentsToSearch[i].complianceResolutionData && paymentsToSearch[i].complianceResolutionData?.approved) {
        if (paymentAlreadyAddedIndex > -1) {
          paymentResponseStatusUpdated[paymentAlreadyAddedIndex].status = 'approved'
        } else {
          paymentResponseStatusUpdated.push({
            status: 'approved',
            paymentId: String(paymentsToSearch[i]?._id)
          })
        }
      }

      if (!paymentsToSearch[i].complianceResolutionData && !(paymentsToSearch[i] as any).supportTicket) {
        if (paymentAlreadyAddedIndex > -1) {
          paymentResponseStatusUpdated[paymentAlreadyAddedIndex].status = 'without-starting'
        } else {
          paymentResponseStatusUpdated.push({
            status: 'without-starting',
            paymentId: String(paymentsToSearch[i]?._id)
          })
        }
      }

      if (!paymentsToSearch[i].complianceResolutionData && (paymentsToSearch[i] as any).supportTicket) {
        if (paymentAlreadyAddedIndex > -1) {
          paymentResponseStatusUpdated[paymentAlreadyAddedIndex].status = (paymentsToSearch[i] as any).supportTicket && (paymentsToSearch[i] as any).supportTicket.hasToResponse === 'client' ? 'pending' : 'with-response'
        } else {
          paymentResponseStatusUpdated.push({
            paymentId: String(paymentsToSearch[i]?._id),
            status: (paymentsToSearch[i] as any).supportTicket && (paymentsToSearch[i] as any).supportTicket.hasToResponse === 'client' ? 'pending' : 'with-response'
          })
        }
      }
    }

    setPaymentsResponseStatus([...paymentResponseStatusUpdated])
  }

  const handleSetPaymentToSearch = (paymentToSearch: string, showError: boolean) => {
    currentListPageRef.current = 1
    paymentToSearchRef.current = paymentToSearch

    setErrors({})
    setCurrentListPage(1)
    setPaymentToSearch(paymentToSearch)
    setSearchingInDatabase(false)
    setNoRegistriesFoundAtDatabase(false)

    if (paymentToSearch === '') {
      const firstPageIndex = (currentListPage - 1) * pageSize
      const lastPageIndex = firstPageIndex + pageSize
      const currentPaymentsDisplayedData = paymentsDisplayedDataRef.current ?? []
      const paymentsPerPage = currentPaymentsDisplayedData.slice(firstPageIndex, lastPageIndex)

      setPaymentsListData(paymentsPerPage)
      setSearchedPaymentsData([])

      if (showError) setErrors({ paymentToSearch: t('searchField.errors.emptyField', { ns: 'payments-to-review-management' }) })
      return
    }

    const currentFullPaymentsData = [...(paymentsDisplayedDataRef.current as Array<Payment>)]
    const filteredPaymentsHistory = currentFullPaymentsData.filter((paymentData) => {
      const paymentDateText = format(new Date(paymentData.createdAt), 'dd/MM/yyyy | HH:mm:ss')
      const paymentStatusText =
        paymentData.status === 'validated'
          ? t('paymentStatus.validated', { ns: 'customer-details-management' })
          : paymentData.status === 'reviewed'
            ? t('paymentStatus.reviewed', { ns: 'customer-details-management' })
            : paymentData.status === 'for-review'
              ? t('paymentStatus.forReview', { ns: 'customer-details-management' })
              : paymentData.status === 'waiting-for-resolution'
                ? t('paymentStatus.waitingForResolution', { ns: 'customer-details-management' })
                : paymentData.status === 'register-disperse-completed' || paymentData.status === 'processing'
                  ? t('paymentStatus.processing', { ns: 'customer-details-management' })
                  : t('paymentStatus.paid', { ns: 'customer-details-management' })

      const paymentDate = handleIsValidMatch(paymentToSearch.toLowerCase(), String(paymentDateText).toLowerCase())
      const paymentStatus = handleIsValidMatch(paymentToSearch.toLowerCase(), String(paymentStatusText).toLowerCase())
      const paymentReference = handleIsValidMatch(paymentToSearch.toLowerCase(), String(paymentData.referenceNumber).toLowerCase())
      const paymentBeneficiary = handleIsValidMatch(paymentToSearch.toLowerCase(), String(paymentData.customerRegisteredFullName).toLowerCase())

      return paymentReference || paymentBeneficiary || paymentStatus || paymentDate
    })

    const firstPageIndex = (currentListPageRef.current - 1) * pageSize
    const lastPageIndex = firstPageIndex + pageSize
    const paymentsPerPage = filteredPaymentsHistory.slice(firstPageIndex, lastPageIndex)

    setPaymentsListData(paymentsPerPage)
    setSearchedPaymentsData(filteredPaymentsHistory)
  }

  const handleSearchPaymentWithAlertsDataByRegex = async () => {
    try {
      setErrors({})
      setSearchingInDatabase(true)
      const {
        data: { accessToken, refreshToken }
      } = await requestAccessToken(String(isAuth).replaceAll('"', ''))
      activateAuth?.({ token: refreshToken })

      const response = await getPaymentsWithAlertsBySearch({ paymentToSearch, belongsTo: String(customerCompanyId) }, accessToken)
      console.log('response', response.data.docs)

      if (response.data.docs.length === 0) {
        setSearchingInDatabase(false)
        setNoRegistriesFoundAtDatabase(true)
        return
      }

      const paymentsWithFindingsAtSearch = response.data.docs.filter(
        (paymentData: Payment) =>
          paymentData.status !== 'waiting-for-resolution' && !('complianceResolutionData' in paymentData) && (!('tags' in paymentData) || (Array.isArray(paymentData.tags) && paymentData.tags.length === 0))
      )

      const paymentsWithGeneralAlertsAtSearch = response.data.docs.filter(
        (paymentData: Payment) =>
          Array.isArray(paymentData.tags) &&
          paymentData.tags.length > 0 &&
          !!paymentData.tags?.some((tag) => tag.type === PaymentTagType.TO_CONFIRM_ORIGIN_OF_FUNDS_MONTHLY || tag.type === PaymentTagType.TO_CONFIRM_ORIGIN_OF_FUNDS_WEEKLY)
      )

      const paymentsWithHardStopAtSearch = response.data.docs.filter(
        (paymentData: Payment) =>
          Array.isArray(paymentData.tags) &&
          paymentData.tags.length > 0 &&
          !!paymentData.tags?.some(
            (tag) => tag.type === PaymentTagType.BLACK_LISTED || tag.type === PaymentTagType.TO_CONFIRM_ORIGIN_OF_FUNDS_DAILY || tag.type === PaymentTagType.TO_CONFIRM_PREVIOUS_PAYMENT_WAITING_FOR_RESOLUTION
          )
      )

      const paymentsWithFindingsUpdated = [...paymentsWithFindingsData]
      const paymentsWithHardStopUpdated = [...paymentsWithHardStopData]
      const paymentsWithGeneralAlertsUpdated = [...paymentsWithGeneralAlertsData]

      for (let i = 0; i < paymentsWithFindingsAtSearch.length; i++) {
        const paymentAlreadyAddedIndex = paymentsWithFindingsData.findIndex((payment) => String(payment._id) === String(paymentsWithFindingsAtSearch[i]._id))

        if (paymentAlreadyAddedIndex > -1) {
          paymentsWithFindingsUpdated[paymentAlreadyAddedIndex] = paymentsWithFindingsAtSearch[i]
        } else {
          paymentsWithFindingsUpdated.push(paymentsWithFindingsAtSearch[i])
        }
      }

      for (let i = 0; i < paymentsWithGeneralAlertsAtSearch.length; i++) {
        const paymentAlreadyAddedIndex = paymentsWithGeneralAlertsData.findIndex((payment) => String(payment._id) === String(paymentsWithGeneralAlertsAtSearch[i]._id))

        if (paymentAlreadyAddedIndex > -1) {
          paymentsWithGeneralAlertsUpdated[paymentAlreadyAddedIndex] = paymentsWithGeneralAlertsAtSearch[i]
        } else {
          paymentsWithGeneralAlertsUpdated.push(paymentsWithGeneralAlertsAtSearch[i])
        }
      }

      for (let i = 0; i < paymentsWithHardStopAtSearch.length; i++) {
        const paymentAlreadyAddedIndex = paymentsWithHardStopData.findIndex((payment) => String(payment._id) === String(paymentsWithHardStopAtSearch[i]._id))

        if (paymentAlreadyAddedIndex > -1) {
          paymentsWithHardStopUpdated[paymentAlreadyAddedIndex] = paymentsWithHardStopAtSearch[i]
        } else {
          paymentsWithHardStopUpdated.push(paymentsWithHardStopAtSearch[i])
        }

        handleGetTicketResponseStatus([paymentsWithHardStopAtSearch[i]])
      }

      setPaymentsWithFindingsData(paymentsWithFindingsUpdated)
      setPaymentsWithGeneralAlertsData(paymentsWithGeneralAlertsUpdated)
      setPaymentsWithHardStopData(paymentsWithHardStopUpdated)

      setPaymentsDisplayedData(response.data.docs)
      paymentsDisplayedDataRef.current = response.data.docs
      setSearchingInDatabase(false)
      setNoRegistriesFoundAtDatabase(false)
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setSearchingInDatabase(false)
      return
    }
  }

  const handleCloseSession = () => {
    removeAuth?.()
    navigate('/', { replace: true, state: { sessionExpired: true } })
  }

  return (
    <div className="section-container">
      <Breadcrumbs
        separator={
          <img
            src={NextIcon}
            alt="next-icon"
          />
        }
        sx={{ width: '100%' }}
        aria-label="breadcrumb"
      >
        <ListItemButton
          sx={{
            '&.MuiButtonBase-root': {
              padding: '0px'
            },
            '&.MuiButtonBase-root:hover': {
              backgroundColor: 'rgba(0,0,0,0)'
            }
          }}
          onClick={() => navigate('/customer-companies')}
        >
          <Typography
            key="3"
            className="breadcrumb-inactive"
          >
            {t('breadcrumbs.firstLevel')}
          </Typography>
        </ListItemButton>
        <ListItemButton
          sx={{
            '&.MuiButtonBase-root': {
              padding: '0px'
            },
            '&.MuiButtonBase-root:hover': {
              backgroundColor: 'rgba(0,0,0,0)'
            }
          }}
          onClick={() => navigate(`/customer-company-details/${customerCompanyId}`)}
        >
          <Typography
            key="3"
            className="breadcrumb-inactive"
          >
            {t('breadcrumbs.secondLevel')}
          </Typography>
        </ListItemButton>
        {customerCompanyData && (
          <Typography
            key="3"
            className="breadcrumb-active"
          >
            {String(customerCompanyData?.businessCompany?.name)}
          </Typography>
        )}
        <Typography
          key="3"
          className="breadcrumb-active"
        >
          {t('breadcrumbs.fifthLevel')}
        </Typography>
      </Breadcrumbs>
      <div
        ref={containerRef}
        className={`pockets-submenu-container ${hasMultipleRows ? 'has-multiple-rows' : ''}`}
      >
        <div
          onClick={() => {
            selectedMainFilterRef.current = PaymentWithNoveltiesType.WITH_FINDINGS
            setSelectedMainFilter(PaymentWithNoveltiesType.WITH_FINDINGS)
          }}
          className={`button-item ${selectedMainFilter === PaymentWithNoveltiesType.WITH_FINDINGS ? 'button-item-selected' : ''}`}
        >
          {`${t('filtersButtons.withFindings', { ns: 'payments-to-review-management' }).toUpperCase()}`}
        </div>
        <div
          onClick={() => {
            selectedMainFilterRef.current = PaymentWithNoveltiesType.GENERAL_ALERT
            setSelectedMainFilter(PaymentWithNoveltiesType.GENERAL_ALERT)
          }}
          className={`button-item ${selectedMainFilter === PaymentWithNoveltiesType.GENERAL_ALERT ? 'button-item-selected' : ''}`}
        >
          {`${t('filtersButtons.generalAlert', { ns: 'payments-to-review-management' }).toUpperCase()}`}
        </div>
        <div
          onClick={() => {
            selectedMainFilterRef.current = PaymentWithNoveltiesType.HARD_STOP
            setSelectedMainFilter(PaymentWithNoveltiesType.HARD_STOP)
          }}
          className={`button-item ${selectedMainFilter === PaymentWithNoveltiesType.HARD_STOP ? 'button-item-selected' : ''}`}
        >
          {`${t('filtersButtons.hardStop', { ns: 'payments-to-review-management' }).toUpperCase()}`}
        </div>
      </div>
      <div
        style={{ marginTop: '20px' }}
        className="customer-company-fundings-history-container"
      >
        <div className="actions-bar">
          <div className="left-content">
            <div className="title">
              {
                paymentsDisplayedData.length === 0 ? t('actionsBar.thereIsNoPaymentsToShow') : paymentsDisplayedData.length > 0 && t('actionsBar.lastPaymentsHistory')
                // t('actionsBar.paymentsWithAlertsHistory', { paymentsWithAlertsCount: paymentToSearchRef.current !== '' ? searchedPaymentsData.length : paymentsDisplayedData.length })
              }
            </div>
          </div>
          <TextField
            variant="standard"
            sx={textFieldWhiteBg}
            label={t('searchField.paymentPlaceholder')}
            value={paymentToSearch}
            error={!!errors.paymentToSearch}
            helperText={errors.paymentToSearch}
            className="search-input"
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                handleSetPaymentToSearch(paymentToSearch, true)
              }
            }}
            onChange={(event) => handleSetPaymentToSearch(event.target.value, true)}
            onBlur={() => {
              if (paymentToSearch === '') setErrors({})
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    aria-label="toggle password visibility"
                    sx={{ color: '#107569', right: '10px', bottom: '3px' }}
                  >
                    <img
                      src={SearchIcon}
                      alt="search-icon"
                    />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </div>
      </div>
      <div className="table-header-container">
        <div className="customer-company-table-column customer-company-table-width-10 display-none-xlg table-header-text">{t('tableHeaders.date')}</div>
        <div className="customer-company-table-column customer-company-table-width-45 table-header-text">{t('tableHeaders.beneficiary')}</div>
        <div className="customer-company-table-column customer-company-table-width-15 table-header-text customer-company-display-none-lg">{t('tableHeaders.amount')}</div>
        <div className="customer-company-table-column customer-company-table-width-20 table-header-text customer-company-display-none-sm">{t('tableHeaders.referenceNumber')}</div>
        <div className="customer-company-table-column customer-company-table-width-10 table-header-text">{t('tableHeaders.status')}</div>
      </div>
      {loadingLocalData ? (
        <div className="users-management-users-list-container">
          <div className="users-management-user-data-container-loader"></div>
          <div className="users-management-user-data-container-loader"></div>
          <div className="users-management-user-data-container-loader"></div>
          <div className="users-management-user-data-container-loader"></div>
          <div className="users-management-user-data-container-loader"></div>
        </div>
      ) : (
        <>
          <div className="documents-list-container">
            {paymentsListData.map((paymentData: Payment, index: number) => {
              return (
                <DocumentPaymentListItem
                  key={index}
                  showDateData
                  showCustomerData
                  topDistance={topDistance}
                  paymentData={paymentData}
                  selectedMainFilter={selectedMainFilter}
                  paymentsResponseStatus={paymentsResponseStatus}
                  handleOpenPaymentDetailsModal={(selectedPaymentData: Partial<Payment>) => {
                    navigate(`/payment-with-alerts-details/${String(selectedPaymentData._id)}/${customerCompanyId}`)
                  }}
                />
              )
            })}
          </div>
          {paymentToSearch !== '' && paymentsListData.length === 0 && (
            <div className="customer-no-data-found-container">
              {searchingInDatabase === false ? (
                <>
                  {noRegistriesFoundAtDatabase === false ? (
                    <>
                      <div className="customer-no-data-found-no-registries-text">
                        {t('searchingResultTexts.case1.secondPart')}{' '}
                        <strong
                          className="customer-no-data-found-no-registries-text-click"
                          onClick={() => handleSearchPaymentWithAlertsDataByRegex()}
                        >
                          {t('searchingResultTexts.case1.thirdPart')}
                        </strong>{' '}
                        {t('searchingResultTexts.case1.fourthPart')}
                      </div>
                      <Button
                        sx={baseButtonBlueBg}
                        onClick={handleSearchPaymentWithAlertsDataByRegex}
                      >
                        {t('searchingResultTexts.expandSearch', { ns: 'customer-company-details-management' })}
                      </Button>
                      {errors.generalError && (
                        <div
                          style={{ marginTop: '0px' }}
                          className="error-message"
                        >
                          {errors.generalError}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="customer-no-data-found-no-registries-text">
                        <img
                          src={HelpIcon}
                          alt="help-icon"
                        />
                      </div>
                      <div className="customer-no-data-found-no-registries-text">{t('searchingResultTexts.case2')}</div>
                    </>
                  )}
                </>
              ) : (
                <>
                  <div className="customer-no-data-found-searching-progress-bar">
                    <LinearProgress />
                  </div>
                  <div className="customer-no-data-found-no-registries-text">{t('searchingResultTexts.case3')}</div>
                </>
              )}
            </div>
          )}
          <Pagination
            pageSize={pageSize}
            currentPage={currentListPage}
            handlePaginationPageChange={(page: number) => {
              setTimeout(() => {
                setScrollToTop(true)
                setCurrentListPage(page)
                currentListPageRef.current = page
              }, 100)
            }}
            handleChangePageSize={(pageSizeToSet: string) => {
              setScrollToTop(true)
              setPageSize(Number(pageSizeToSet))
            }}
            totalCount={paymentToSearchRef.current !== '' ? searchedPaymentsData.length : paymentsDisplayedData.length}
          />
        </>
      )}
    </div>
  )
}

export default PaymentsWithAlertsManagement
